import store from '@/store'

/**
 * 返回当前生效的时区偏移（分钟，正东负西，与 -getTimezoneOffset() 同号）。
 * 全站 5 类 timezone touch-point 都通过此函数取值，便于后续扩展。
 *
 * mode='auto'  → 浏览器实际偏移
 * mode='fixed' → 用户在「平台设置」配置的 offsetMinutes
 * 异常回落    → 浏览器偏移（确保任何故障都不阻断登录）
 */
export function getEffectiveTimezone() {
  try {
    const cfg = store && store.getters && store.getters.timezoneSettingData
    if (cfg && cfg.mode === 'fixed' && typeof cfg.offsetMinutes === 'number') {
      return cfg.offsetMinutes
    }
  } catch (e) {
    // store 未初始化等异常静默回落
  }
  return -new Date().getTimezoneOffset()
}

/**
 * 浏览器当前时区偏移（用于「跟随浏览器」选项的 label 实时显示）。
 */
export function getBrowserTimezone() {
  return -new Date().getTimezoneOffset()
}

/**
 * 把分钟偏移格式化为 "(UTC±HH:MM)"。
 *
 * 示例：480 → "(UTC+08:00)"，-330 → "(UTC-05:30)"，345 → "(UTC+05:45)"
 */
export function formatOffsetLabel(minutes) {
  const sign = minutes >= 0 ? '+' : '-'
  const abs = Math.abs(minutes)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `(UTC${sign}${hh}:${mm})`
}

/**
 * 在配置时区下，把指定时刻的"日期"取出，时分秒固定为给定值。
 * 用于"今天 00:00:00" / "今天 23:59:59" / "昨天 00:00:00" 这类边界字符串。
 *
 * @param {Date|null} fromDate - 基准时刻，默认 new Date()
 * @param {string} timeStr - 时分秒字符串，默认 '00:00:00'
 * @param {number} dayOffset - 日偏移（往前为负、往后为正），默认 0
 * @returns {string} 'yyyy-MM-dd HH:mm:ss' 格式
 */
export function formatBoundaryStr(fromDate, timeStr, dayOffset) {
  if (fromDate == null) fromDate = new Date()
  if (timeStr == null) timeStr = '00:00:00'
  if (dayOffset == null) dayOffset = 0
  const offset = getEffectiveTimezone()
  // 把"现在"shift 到一个虚拟 UTC 时刻：让 .getUTCxxx() 读出来正好是配置时区的 wall clock
  const shifted = new Date(fromDate.getTime() + offset * 60000 + dayOffset * 86400000)
  const yyyy = shifted.getUTCFullYear()
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(shifted.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${timeStr}`
}

/**
 * 在配置时区下当前时刻（或减去 N 天）的完整时间戳字符串。
 * 用于"近一天/近 N 天"这类滚动 endpoint。
 *
 * @param {number} daysAgo - 往前推 N 天，默认 0（即"现在"）
 * @returns {string} 'yyyy-MM-dd HH:mm:ss' 格式
 */
export function nowInConfiguredTzStr(daysAgo) {
  if (daysAgo == null) daysAgo = 0
  const offset = getEffectiveTimezone()
  const shifted = new Date(new Date().getTime() + offset * 60000 - daysAgo * 86400000)
  const yyyy = shifted.getUTCFullYear()
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(shifted.getUTCDate()).padStart(2, '0')
  const hh = String(shifted.getUTCHours()).padStart(2, '0')
  const mi = String(shifted.getUTCMinutes()).padStart(2, '0')
  const ss = String(shifted.getUTCSeconds()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
}
